import React, { useEffect, useState } from "react";
import { PhoneCall } from "lucide-react";
import { getDriverContact, tel } from "@/lib/driverContact";

// Persistent "Speak to a human" affordance. Relay supports the human service —
// it never replaces it. Always reachable via a floating button on driver screens;
// routes to the driver's own customer support hotline. Falls back to the Emergency
// page when no hotline is configured, so the action is never a dead end.
export function SpeakToHuman() {
  const [contact, setContact] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => { let m = true; getDriverContact().then((c) => m && setContact(c)).catch(() => {}); return () => { m = false; }; }, []);

  const support = contact?.support_phone;
  const emergency = contact?.emergency_phone;

  return (
    <>
      <button
        type="button"
        data-testid="speak-to-human-btn"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 h-9 pl-2.5 pr-3 rounded-full bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/15 transition-colors"
        aria-label="Speak to a human"
      >
        <PhoneCall className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />
        <span className="hidden xs:inline sm:inline">Speak to a human</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/40 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="speak-to-human-sheet">
          <div className="w-full max-w-md bg-card rounded-t-2xl border-t border-border p-5 pb-8 vda-rise" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-border" aria-hidden="true" />
            <h2 className="text-lg font-semibold">Speak to your Venson team</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {contact?.customer_name ? `${contact.customer_name} driver support` : "We're here to help with your vehicle."}
            </p>
            <div className="mt-4 space-y-2">
              {support ? (
                <a href={`tel:${tel(support)}`} data-testid="speak-support-call"
                  className="flex items-center justify-between h-12 px-4 rounded-[6px] bg-primary text-primary-foreground font-medium">
                  <span className="flex items-center gap-2"><PhoneCall className="h-4 w-4" /> Call driver support</span>
                  <span className="vda-reg">{support}</span>
                </a>
              ) : (
                <p className="text-sm text-muted-foreground border border-dashed border-border rounded-[6px] p-3">
                  No support line is configured for your account yet. Please use the Emergency tab or contact your fleet team.
                </p>
              )}
              {emergency && (
                <a href={`tel:${tel(emergency)}`} data-testid="speak-emergency-call"
                  className="flex items-center justify-between h-12 px-4 rounded-[6px] border border-[hsl(var(--vda-red))]/30 text-[hsl(var(--vda-red))] font-medium">
                  <span className="flex items-center gap-2"><PhoneCall className="h-4 w-4" /> Emergency line</span>
                  <span className="vda-reg">{emergency}</span>
                </a>
              )}
            </div>
            <button type="button" data-testid="speak-close" onClick={() => setOpen(false)}
              className="mt-4 w-full h-10 rounded-[6px] border border-[hsl(var(--input))] text-sm font-medium">
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}
